// FORM: https://github.com/modusign/modusign-client/blob/1f2cc00e317b3cc6ddf42ace319c72036470a423/app/src/components/RequesterImageField/index.tsx

import { ImageField } from '@modusign/react-signing/types';
import cx from 'classnames';
import { useCallback, useMemo, useState } from 'react';

import RoundedXButton from '@app/components/Buttons/RoundedXButton';
import {
  toAbsolutePoint,
  toAbsoluteSize,
  toRelativePoint,
  toRelativeSize,
} from '@app/features/apply-react-signing/components/FieldRenderer/libs/absRelConverter';
import { createAbsoluteSize } from '@app/features/apply-react-signing/components/FieldRenderer/libs/createAbsoluteSize';
import UniversalImageField from '@app/features/apply-react-signing/components/FieldRenderer/Setup/components/UniversalImageField';
import {
  AbsolutePoint,
  AbsoluteSize,
} from '@app/features/apply-react-signing/components/FieldRenderer/types';
import useResponsiveValue from '@app/hooks/useResponsiveValue';

import { RequesterFieldBaseProps } from '../props';
import RequesterImageFieldContextMenu from './RequesterImageFieldContextMenu';

import './RequesterImageField.scss';

interface RequesterImageFieldProps extends RequesterFieldBaseProps<ImageField> {
  isError?: boolean;
  isLoading?: boolean;
  imageSrc?: string;
  onImageChange: (value: { imageSrc: string; fileName: string }) => void;
}

export function RequesterImageField({
  field,
  imageSrc,
  viewport,
  readOnly,
  isPreview,
  initialContextMenuOpened,
  isDataLabelEditable,
  isDataLabelDuplicated,
  isError,
  isLoading,
  onSizeChange,
  onPointChange,
  onImageChange,
  onFieldRemove,
  onDataLabelChange,
}: RequesterImageFieldProps) {
  const isInMobileBreakpoint = useResponsiveValue([true, false]);
  const [contextMenuOpen, setContextMenuOpen] = useState(initialContextMenuOpened);
  const [focused, setFocused] = useState(false);

  const isImageChangeable =
    !field.url &&
    !readOnly &&
    !isInMobileBreakpoint &&
    ((isDataLabelEditable && contextMenuOpen) || !isDataLabelEditable);

  const dataLabelErrorMessage = useMemo(() => {
    if (!field.dataLabel.trim()) {
      return '데이터 라벨 입력은 필수입니다.';
    }
    if (isDataLabelDuplicated) {
      return '중복된 데이터 라벨입니다.';
    }
    return '';
  }, [isDataLabelDuplicated, field.dataLabel]);

  const handlePointChange = useCallback(
    (point: AbsolutePoint) => {
      const relative = toRelativePoint(point, viewport);
      onPointChange(relative);
    },
    [onPointChange, viewport]
  );

  const handleSizeChange = useCallback(
    (size: AbsoluteSize) => {
      const relative = toRelativeSize(size, viewport);
      onSizeChange(relative);
    },
    [onSizeChange, viewport]
  );

  return (
    <>
      <UniversalImageField
        boxClassName={isPreview ? 'RequesterImageField__box--preview' : 'RequesterImageField__box'}
        draggable={!readOnly && !isInMobileBreakpoint}
        imageSrc={imageSrc}
        isError={isError}
        isImageChangeable={isImageChangeable}
        isLoading={isLoading}
        point={toAbsolutePoint(field.position, viewport)}
        resizable={!readOnly && !isInMobileBreakpoint}
        rootClassName={cx({
          RequesterImageField: true,
          // eslint-disable-next-line camelcase
          RequesterImageField__error: isError,
          'RequesterImageField--image-selected': imageSrc && !readOnly && !isInMobileBreakpoint,
          'RequesterImageField--focused': focused,
        })}
        size={toAbsoluteSize(field.size, viewport)}
        viewport={createAbsoluteSize(viewport)}
        onBlur={() => setFocused(false)}
        onClick={() => setContextMenuOpen(true)}
        onFocus={() => setFocused(true)}
        onImageChange={onImageChange}
        onOutsideClick={() => {
          if (field.dataLabel && !isDataLabelDuplicated) {
            setContextMenuOpen(false);
          }
        }}
        onPointChange={handlePointChange}
        onSizeChange={handleSizeChange}
      >
        {!readOnly && !isInMobileBreakpoint && (
          <>
            {isDataLabelEditable && contextMenuOpen && (
              <div className="RequesterImageField__context-menu-container">
                <RequesterImageFieldContextMenu
                  dataLabel={field.dataLabel}
                  dataLabelErrorMessage={dataLabelErrorMessage}
                  onChange={onDataLabelChange}
                />
              </div>
            )}
            {!isLoading && (
              <div className="RequesterImageField__delete-button-box">
                <RoundedXButton onClick={() => onFieldRemove(field.id)} />
              </div>
            )}
          </>
        )}
      </UniversalImageField>
    </>
  );
}
